/**
 * The end of a role assignment given "until a date" (Phase 2 W5-1; docs/plans/PHASE_2_W5.md §2;
 * owner O51, O65). A daily-wage worker's role must not end in the middle of a night shift, so it
 * ends on the morning after the chosen date, at the location's workday end hour
 * (`org.workday_end_hour`), Baghdad time. The server computes it with this function when the role
 * is given or renewed and stores it; the admin web shows the exact time before saving.
 */
import { z } from 'zod';

/** Iraq has kept UTC+3 all year since 2008: no daylight saving to account for. */
const BAGHDAD_OFFSET_MS = 3 * 3_600_000;
const DAY_MS = 86_400_000;

export const WORKDAY_END_HOUR_SETTING = 'org.workday_end_hour';
export const WORKDAY_END_HOUR_MIN = 0;
export const WORKDAY_END_HOUR_MAX = 12;
/** An end date at most this many days ahead (two years: a season, a long contract). */
export const ROLE_END_MAX_DAYS = 730;

/** `YYYY-MM-DD`, a real calendar date. */
export const RoleEndsOnSchema = z.iso.date();

function dayNumber(date: string): number {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!parts) throw new RangeError(`Not a date: ${date}`);
  const [, y, m, d] = parts;
  const ms = Date.UTC(Number(y), Number(m) - 1, Number(d));
  const back = new Date(ms).toISOString().slice(0, 10);
  if (back !== date) throw new RangeError(`Not a date: ${date}`);
  return ms / DAY_MS;
}

/** The calendar date in Baghdad of an instant (`YYYY-MM-DD`). */
export function baghdadDateOf(at: Date | number): string {
  const ms = typeof at === 'number' ? at : at.getTime();
  return new Date(ms + BAGHDAD_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * The instant a role given "until `endsOn`" ends: the next day at `workdayEndHour`:00 Baghdad time
 * (0 = midnight at the end of `endsOn`). With 6, "until 2026-10-10" ends 2026-10-11 06:00 Baghdad,
 * that is 03:00 UTC.
 */
export function roleEndAt(endsOn: string, workdayEndHour: number): Date {
  if (
    !Number.isInteger(workdayEndHour) ||
    workdayEndHour < WORKDAY_END_HOUR_MIN ||
    workdayEndHour > WORKDAY_END_HOUR_MAX
  ) {
    throw new RangeError(`Workday end hour out of range: ${String(workdayEndHour)}`);
  }
  return new Date(
    (dayNumber(endsOn) + 1) * DAY_MS + workdayEndHour * 3_600_000 - BAGHDAD_OFFSET_MS,
  );
}

export type RoleEndProblem = 'past' | 'too_far';

/**
 * Whether `endsOn` may be chosen at `now`: not before today in Baghdad, and at most
 * `ROLE_END_MAX_DAYS` ahead. (Any such date ends after `now`: the earliest end is midnight tonight.)
 */
export function roleEndProblem(endsOn: string, now: Date | number): RoleEndProblem | null {
  const day = dayNumber(endsOn);
  const today = dayNumber(baghdadDateOf(now));
  if (day < today) return 'past';
  if (day - today > ROLE_END_MAX_DAYS) return 'too_far';
  return null;
}

/** A role assignment in force at `at` (the same rule as the database's: from ≤ at < to). */
export function assignmentInForce(
  assignment: { readonly validFrom?: string | null; readonly validTo?: string | null },
  at: Date | number,
): boolean {
  const ms = typeof at === 'number' ? at : at.getTime();
  const from = assignment.validFrom ? Date.parse(assignment.validFrom) : -Infinity;
  const to = assignment.validTo ? Date.parse(assignment.validTo) : Infinity;
  return from <= ms && ms < to;
}
